import type { ShopProduct, ShopProductVariant } from "@mall-mart/shared";
import { buildShopifyCartPermalink } from "./shopifyPermalink.js";
import type { Session } from "../auth/session.js";

export interface CartItem {
  variantId: string;
  productTitle: string;
  variantTitle: string;
  price: string;
  currency: string;
  imageUrl: string;
  quantity: number;
}

export type CheckoutMode = "permalink" | "cart_api";

export interface CartOptions {
  shopId: string;
  shopName: string;
  mode: CheckoutMode;
  /** Merchant storefront origin — required for permalink mode. */
  storeOrigin?: string;
  /** Mall Mart server base URL (http/https) — required for cart_api mode. */
  serverHttpUrl?: string;
  /** Live view of the signed-in session; null/absent for guests. Signed-in
   *  checkouts carry the shopper's email (pre-fill + customer record). */
  sessionProvider?: () => Session | null;
}

/**
 * Per-shop cart held entirely client-side. Checkout is per-source:
 *  - permalink (acp-feed stores): open the merchant's Online Store cart URL.
 *  - cart_api (MartClub stores): POST /api/checkout, server returns an
 *    Online Store cart permalink on the shared store origin.
 */
export class Cart {
  readonly shopId: string;
  readonly shopName: string;
  readonly mode: CheckoutMode;
  private readonly storeOrigin: string | undefined;
  private readonly serverHttpUrl: string | undefined;
  private items: Map<string, CartItem> = new Map();
  private listeners: Array<(cart: Cart) => void> = [];
  private readonly sessionProvider: () => Session | null;

  constructor(options: CartOptions) {
    this.sessionProvider = options.sessionProvider ?? ((): Session | null => null);
    this.shopId = options.shopId;
    this.shopName = options.shopName;
    this.mode = options.mode;
    this.storeOrigin = options.storeOrigin;
    this.serverHttpUrl = options.serverHttpUrl;
    if (this.mode === "permalink" && this.storeOrigin === undefined) {
      throw new Error(`Cart(${this.shopId}): permalink mode requires storeOrigin`);
    }
    if (this.mode === "cart_api" && this.serverHttpUrl === undefined) {
      throw new Error(`Cart(${this.shopId}): cart_api mode requires serverHttpUrl`);
    }
  }

  add(product: ShopProduct, variant: ShopProductVariant): void {
    const existing: CartItem | undefined = this.items.get(variant.id);
    if (existing !== undefined) {
      existing.quantity += 1;
    } else {
      this.items.set(variant.id, {
        variantId: variant.id,
        productTitle: product.title,
        variantTitle: variant.title,
        price: variant.price,
        currency: variant.currency,
        imageUrl: variant.imageUrl,
        quantity: 1,
      });
    }
    this.emit();
  }

  /** Swap a line to a different variant of the same product, keeping its
   *  quantity. Merges into an existing line when the target variant is
   *  already in the cart. */
  changeVariant(oldVariantId: string, product: ShopProduct, variant: ShopProductVariant): void {
    const existing: CartItem | undefined = this.items.get(oldVariantId);
    if (existing === undefined || oldVariantId === variant.id) {
      return;
    }
    this.items.delete(oldVariantId);
    const target: CartItem | undefined = this.items.get(variant.id);
    if (target !== undefined) {
      target.quantity += existing.quantity;
    } else {
      this.items.set(variant.id, {
        variantId: variant.id,
        productTitle: product.title,
        variantTitle: variant.title,
        price: variant.price,
        currency: variant.currency,
        imageUrl: variant.imageUrl,
        quantity: existing.quantity,
      });
    }
    this.emit();
  }

  remove(variantId: string): void {
    if (this.items.delete(variantId)) {
      this.emit();
    }
  }

  clear(): void {
    if (this.items.size === 0) {
      return;
    }
    this.items.clear();
    this.emit();
  }

  count(): number {
    let total: number = 0;
    for (const i of this.items.values()) {
      total += i.quantity;
    }
    return total;
  }

  itemList(): CartItem[] {
    return Array.from(this.items.values());
  }

  totalPrice(): { amount: string; currency: string } {
    let amount: number = 0;
    let currency: string = "USD";
    for (const i of this.items.values()) {
      const price: number = parseFloat(i.price);
      if (!Number.isNaN(price)) {
        amount += price * i.quantity;
      }
      currency = i.currency;
    }
    return { amount: amount.toFixed(2), currency };
  }

  /** Replace the cart contents wholesale (restore from the server-side
   *  persistent cart). One change event regardless of item count. */
  restoreItems(items: CartItem[]): void {
    this.items.clear();
    for (const item of items) {
      this.items.set(item.variantId, { ...item });
    }
    this.emit();
  }

  async resolveCheckoutUrl(): Promise<string> {
    const items: Array<{ variantId: string; quantity: number }> = Array.from(
      this.items.values()
    ).map((i) => ({ variantId: i.variantId, quantity: i.quantity }));
    const session: Session | null = this.sessionProvider();
    if (this.mode === "permalink") {
      return buildShopifyCartPermalink(this.storeOrigin as string, items, session?.email);
    }
    const res: Response = await fetch(`${this.serverHttpUrl}/api/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        shopId: this.shopId,
        items,
        ...(session !== null ? { email: session.email, token: session.token } : {}),
      }),
    });
    if (!res.ok) {
      throw new Error(`POST /api/checkout failed: ${res.status}`);
    }
    const data: { checkoutUrl: string } = await res.json();
    return data.checkoutUrl;
  }

  onChange(fn: (cart: Cart) => void): void {
    this.listeners.push(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) {
      fn(this);
    }
  }
}
